import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import SearchableSelect from 'components/FormFields/SearchableSelect';
import { useAccountSearch } from '../../CreateEventModal/hooks/useAccountSearch';

// Container
const Container = styled.div``;

// Section for account selection
const AccountSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 16px 0;
`;

const SelectWrapper = styled.div`
  max-width: 400px;
`;

// Divider
const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) =>
    theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  margin: 24px 0;
`;

// Toggle row styles (matching PaymentsTab from Accounts)
const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: ${({ theme }) =>
    theme?.colors?.background?.overlay ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)')};
  border-radius: 8px;
  margin-top: ${({ first }) => (first ? '0' : '12px')};

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto auto;
    grid-template-areas:
      'icon label toggle'
      'description description description';
    gap: 6px 10px;
    align-items: center;
  }
`;

const ToggleInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    display: contents;
  }
`;

const ToggleIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fa4616;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    grid-area: icon;
    width: 28px;
    height: 28px;
    border-radius: 6px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const ToggleText = styled.div`
  @media (max-width: 768px) {
    display: contents;
  }
`;

const ToggleLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};

  @media (max-width: 768px) {
    grid-area: label;
    font-size: 15px;
    font-weight: 600;
  }
`;

const ToggleDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) =>
    theme?.colors?.textGray?.secondary ||
    (theme?.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-top: 2px;

  @media (max-width: 768px) {
    grid-area: description;
    margin-top: 0;
  }
`;

// Toggle switch
const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 26px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    grid-area: toggle;
  }
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background: linear-gradient(135deg, #fa4616, #ff6b3b);
  }

  &:checked + span:before {
    transform: translateX(22px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgb(226, 232, 240)'};
  transition: all 0.3s ease;
  border-radius: 26px;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 3px;
    top: 3px;
    background-color: #fff;
    transition: all 0.3s ease;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  font-size: 16px;
`;

// Error message for validation
const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(239, 68, 68, 0.1)'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(239, 68, 68, 0.3)'
        : 'rgba(239, 68, 68, 0.3)'};
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  color: #ef4444;
  font-size: 14px;
  font-weight: 500;
`;

const ErrorIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

// Icons
const CreditCardIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const DollarIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const GlobeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const UserCheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <polyline points="17 11 19 13 23 9" />
  </svg>
);

const AccountTab = ({
  accountId,
  onAccountIdChange,
  feeSettings = {},
  onFeeSettingsChange,
  loading,
  feeSettingsError,
}) => {
  const { accounts, loading: accountsLoading } = useAccountSearch();

  const handleToggleChange = (field) => (e) => {
    const isChecked = e.target.checked;

    if (field === 'all_in_pricing') {
      // All In Pricing = hide_fee: true (promoter absorbs)
      const updates = { hide_fee: isChecked };
      if (isChecked) {
        // Mutually exclusive: turn off Cash Back
        updates.marketing_fee = false;
      }
      onFeeSettingsChange({ ...feeSettings, ...updates });
    } else if (field === 'customer_pays_fees') {
      // Customer Pays Fees = hide_fee: false
      const updates = { hide_fee: !isChecked };
      if (!isChecked) {
        // Turning off Customer Pays = All In Pricing ON, so turn off Cash Back
        updates.marketing_fee = false;
      }
      onFeeSettingsChange({ ...feeSettings, ...updates });
    } else {
      onFeeSettingsChange({
        ...feeSettings,
        [field]: isChecked,
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <LoadingContainer>Loading account settings...</LoadingContainer>
      </Card>
    );
  }

  return (
    <Container>
      <Card>
        {/* Account Selection */}
        <AccountSection>
          <SectionTitle>Assigned Account</SectionTitle>
          <SelectWrapper>
            <SearchableSelect
              label="Account"
              value={accountId ? Number(accountId) : null}
              onChange={(option) => {
                onAccountIdChange(option?.value || null);
              }}
              options={accounts.map((acc) => ({
                ...acc,
                value: Number(acc.value),
              }))}
              loading={accountsLoading}
              placeholder="Search accounts..."
            />
          </SelectWrapper>
        </AccountSection>

        <Divider />

        {/* Fee Settings */}
        <SectionTitle>Fee Settings</SectionTitle>

        <ToggleRow first>
          <ToggleInfo>
            <ToggleIcon>
              <DollarIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>All In Pricing</ToggleLabel>
              <ToggleDescription>
                Promoter pays ticket fee and order processing fee
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={!!feeSettings.hide_fee}
              onChange={handleToggleChange('all_in_pricing')}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>

        <ToggleRow>
          <ToggleInfo>
            <ToggleIcon>
              <UserCheckIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>Customer Pays Fees</ToggleLabel>
              <ToggleDescription>
                Customer pays ticket fee and order processing fee
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={!feeSettings.hide_fee}
              onChange={handleToggleChange('customer_pays_fees')}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>

        <ToggleRow>
          <ToggleInfo>
            <ToggleIcon>
              <CreditCardIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>1% Cash Back</ToggleLabel>
              <ToggleDescription>
                Collect 1% for cash back program
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={!!feeSettings.marketing_fee}
              onChange={handleToggleChange('marketing_fee')}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>

        <ToggleRow>
          <ToggleInfo>
            <ToggleIcon>
              <GlobeIcon />
            </ToggleIcon>
            <ToggleText>
              <ToggleLabel>Collect International Card Fees</ToggleLabel>
              <ToggleDescription>
                Collect additional fees incurred for out-of-country cards
              </ToggleDescription>
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={!!feeSettings.international_fee}
              onChange={handleToggleChange('international_fee')}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>

        {feeSettingsError && (
          <ErrorMessage>
            <ErrorIcon>!</ErrorIcon>
            {feeSettingsError}
          </ErrorMessage>
        )}
      </Card>
    </Container>
  );
};

AccountTab.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAccountIdChange: PropTypes.func.isRequired,
  feeSettings: PropTypes.shape({
    marketing_fee: PropTypes.bool,
    international_fee: PropTypes.bool,
    hide_fee: PropTypes.bool,
  }),
  onFeeSettingsChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  feeSettingsError: PropTypes.string,
};

export default AccountTab;
